import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  BREAKPOINTS,
  calculateDeviceCapabilities,
  getSSRFallbackCapabilities
} from './deviceCapabilities.helpers';

/**
 * Screen size breakpoints matching MUI defaults
 */
export type ScreenSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Pointer type based on CSS pointer media query
 * - 'coarse': Touch/stylus (finger-sized targets) - tablets, phones
 * - 'fine': Mouse/trackpad (precise pointer) - desktops, laptops
 * - 'none': No pointing device
 */
export type PointerType = 'coarse' | 'fine' | 'none';

/**
 * Device orientation
 */
export type Orientation = 'portrait' | 'landscape';

/**
 * Device capabilities information
 */
export interface DeviceCapabilities {
  /** True for touch-enabled devices (tablets, phones) */
  isTouchDevice: boolean;
  /** True for tablet-sized screens with touch (iPad, Android tablets) */
  isTablet: boolean;
  /** True for phone-sized screens */
  isMobile: boolean;
  /** True for large screens with fine pointer (desktops, laptops) */
  isDesktop: boolean;
  /** Current screen size category */
  screenSize: ScreenSize;
  /** Pointer type (coarse for touch, fine for mouse) */
  pointerType: PointerType;
  /** Whether the device supports hover (false for touch-only) */
  hasHover: boolean;
  /** Current device orientation */
  orientation: Orientation;
  /** Screen width in pixels */
  screenWidth: number;
  /** Screen height in pixels */
  screenHeight: number;
}

/**
 * Calculate capabilities from browser APIs
 */
function calculateCapabilities(): DeviceCapabilities {
  if (typeof window === 'undefined') {
    return getSSRFallbackCapabilities();
  }

  return calculateDeviceCapabilities({
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    hasCoarsePointer: window.matchMedia('(pointer: coarse)').matches,
    hasFinePointer: window.matchMedia('(pointer: fine)').matches,
    hasHover: window.matchMedia('(hover: hover)').matches,
    maxTouchPoints: navigator.maxTouchPoints
  });
}

const DeviceCapabilitiesContext = createContext<DeviceCapabilities | null>(
  null
);

/**
 * Provider component that detects and provides device capabilities.
 * Should be placed near the root of the application.
 *
 * @example
 * ```tsx
 * <DeviceCapabilitiesProvider>
 *   <App />
 * </DeviceCapabilitiesProvider>
 * ```
 */
export function DeviceCapabilitiesProvider({
  children
}: PropsWithChildren<{}>) {
  const [capabilities, setCapabilities] = useState<DeviceCapabilities>(
    calculateCapabilities
  );

  useEffect(() => {
    function handleChange() {
      setCapabilities(calculateCapabilities());
    }

    // Listen for resize events (orientation change, window resize)
    window.addEventListener('resize', handleChange);

    // Listen for orientation change on mobile devices
    window.addEventListener('orientationchange', handleChange);

    // Also listen to media query changes for pointer type
    const pointerQuery = window.matchMedia('(pointer: coarse)');
    const hoverQuery = window.matchMedia('(hover: hover)');

    pointerQuery.addEventListener('change', handleChange);
    hoverQuery.addEventListener('change', handleChange);

    return () => {
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
      pointerQuery.removeEventListener('change', handleChange);
      hoverQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const value = useMemo(() => capabilities, [capabilities]);

  return (
    <DeviceCapabilitiesContext.Provider value={value}>
      {children}
    </DeviceCapabilitiesContext.Provider>
  );
}

/**
 * Hook to access device capabilities.
 * Must be used within a DeviceCapabilitiesProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isTouchDevice, isTablet, screenSize } = useDeviceCapabilities();
 *
 *   if (isTablet) {
 *     // Render tablet-optimized UI
 *   }
 * }
 * ```
 */
export function useDeviceCapabilities(): DeviceCapabilities {
  const context = useContext(DeviceCapabilitiesContext);

  if (context === null) {
    // Fallback for components outside provider - calculate on demand
    // This allows gradual adoption without breaking existing code
    return calculateCapabilities();
  }

  return context;
}

/**
 * Export breakpoints for external use
 */
export { BREAKPOINTS as DEVICE_BREAKPOINTS };

/**
 * Re-export helpers for testing and advanced use cases
 */
export {
  getScreenSize,
  getOrientation,
  getIsTablet,
  getIsMobile,
  getIsDesktop,
  getPointerTypeFromMatches,
  getIsTouchDevice,
  getSSRFallbackCapabilities,
  calculateDeviceCapabilities
} from './deviceCapabilities.helpers';
